/* eslint-disable sonarjs/no-duplicate-string */
import { type Option, THEME, SPEED_TEXT_MAP } from '../option';
import { type DataItem } from '../core/Dataset';

/**
 * 刻度线
 */
function buildTickLines({ styles, data }: { styles: { [k: string]: string }; data: DataItem[] }) {
  let htmlStr = '';

  data.forEach(() => {
    htmlStr += `
      <div class="js-tickLine-tick js-tickLine-main-tick ${styles['tickLine-tick']}"></div>
    `;
  });

  return htmlStr;
}

/**
 * 两侧刻度值标签
 */
function buildSideTickValues({
  styles,
  option
}: {
  styles: { [k: string]: string };
  option: Option;
}) {
  const htmlStrArray = [] as string[];

  [option.data[0], option.data[option.data.length - 1]].forEach((date, index) => {
    const text = (() => {
      if (
        option.config?.axis?.tickValue?.formatter &&
        typeof option.config?.axis?.tickValue?.formatter === 'function'
      ) {
        return option.config?.axis?.tickValue?.formatter(
          date,
          index === 0 ? index : option.data.length - 1
        );
      }

      // @ts-ignore
      return String(typeof date?.value !== 'undefined' ? date?.value : date);
    })();

    htmlStrArray.push(`
      <div class="js-tickValue-tick ${styles['tickValue-tick']}">
        <span>${text}</span>
      </div>
    `);
  });

  return htmlStrArray;
}

/**
 * 刻度值标签
 */
function buildTickValues({
  styles,
  option,
  data
}: {
  styles: { [k: string]: string };
  option: Option;
  data: DataItem[];
}) {
  let htmlStr = '';

  data.forEach((dateData, index) => {
    const originDate = option.data[index];
    // ! 刻度值不渲染次要刻度
    if (dateData.type !== 'main') {
      return;
    }

    const text = (() => {
      if (
        option.config?.axis?.tickValue?.formatter &&
        typeof option.config?.axis?.tickValue?.formatter === 'function'
      ) {
        return option.config?.axis?.tickValue?.formatter(originDate, index);
      }

      // @ts-ignore
      return String(typeof originDate?.value !== 'undefined' ? originDate?.value : originDate);
    })();

    htmlStr += `
      <div class="js-tickValue-tick ${styles['tickValue-tick']}">
        <span>${text}</span>
      </div>
    `;
  });

  return htmlStr;
}

/**
 * 倍速按钮
 */
function buildSpeedAdjustmentButton({
  styles,
  isPCTheme,
  option,
  speed
}: {
  styles: { [k: string]: string };
  isPCTheme: boolean;
  option: Option;
  speed: keyof typeof SPEED_TEXT_MAP;
}) {
  const enable = option.config?.operation?.speed?.enable;

  return `
    <div style="display:${enable === true ? 'flex' : 'none'}" class="js-speed-adjustment-btn ${
    styles['speed-adjustment-btn']
  }" title="倍速">
      <label>倍速</label>
      <span>${!isPCTheme || Number(speed) !== 1 ? `${SPEED_TEXT_MAP[speed]}X` : '倍速'}</span>
      <section class="js-menu-list ${styles['menu-list']}">
        <div class="js-background-mask ${styles['background-mask']}"></div>
        <ul class="js-menu-list-content">
          <li data-speed="2.0" class="${
            SPEED_TEXT_MAP[speed] === '2.0' ? styles.selected : ''
          }"><span>2.0X</span></li>
          <li data-speed="1.5" class="${
            SPEED_TEXT_MAP[speed] === '1.5' ? styles.selected : ''
          }"><span>1.5X</span></li>
          <li data-speed="1.25" class="${
            SPEED_TEXT_MAP[speed] === '1.25' ? styles.selected : ''
          }"><span>1.25X</span></li>
          <li data-speed="1.0" class="${
            SPEED_TEXT_MAP[speed] === '1.0' ? styles.selected : ''
          }"><span>1.0X</span></li>
          <li data-speed="0.75" class="${
            SPEED_TEXT_MAP[speed] === '0.75' ? styles.selected : ''
          }"><span>0.75X</span></li>
          <li data-speed="0.5" class="${
            SPEED_TEXT_MAP[speed] === '0.5' ? styles.selected : ''
          }"><span>0.5X</span></li>
        </ul>
      </section>
    </div>
  `;
}

/**
 * 轴
 */
function buildAxis({
  styles,
  option,
  data,
  isRange
}: {
  styles: { [k: string]: string };
  option: Option;
  data: DataItem[];
  isRange: boolean;
}) {
  // * 刻度线
  const tickLinesHtml = buildTickLines({
    styles,
    data
  });
  // * 刻度值（两边）
  const sideTickValuesHtml = buildSideTickValues({ styles, option });
  // * 刻度值（所有的）
  const allTickValuesHtml = buildTickValues({
    styles,
    option,
    data
  });

  // prettier-ignore
  return `
    <section class="js-x-axis-box ${styles['x-axis-box']}">

      <section class="${styles['tickValue-side']} ${styles.tickValue}">${
        sideTickValuesHtml[0]
      }</section>

      <div class="${styles['x-axis-position']}">

        <main class="js-x-axis ${styles['x-axis']}">
          <section class="${styles['progress-container']}">
            <div class="js-progress-range ${styles['progress-range']}"></div>
          </section>
          <section class="${styles['progress-container']}">
            <div class="js-progress ${styles.progress}"></div>
          </section>
          <section style="opacity: 0;" class="js-tickLine-container ${
            styles.tickLine
          }">${tickLinesHtml}</section>
        </main>

        <section style="opacity: 0;" class="js-tickValue-all ${styles['tickValue-all']} ${
          styles.tickValue
        }">
          ${allTickValuesHtml}
        </section>

        <section class="js-interaction-area ${styles['interaction-area']}">

          <section class="js-start-range-pointer ${styles.pointer}" style="display: ${
            isRange && !option?.config?.axis?.action?.dataIndexRange?.disableStartBtn ? 'block' : 'none'
          }">
            <div class="js-marker ${styles.marker}"></div>
          </section>

          <section class="js-pointer ${styles.pointer}">
            <div class="js-tooltip-wrapper ${styles['pointer-tooltip-wrapper']}">
              <div class="js-tooltip-arrow ${styles['pointer-tooltip-arrow']}"></div>
              <span class="js-tooltip-text ${styles.tooltip}"></span>
            </div>
            <div class="js-marker ${styles.marker}"></div>
          </section>

        </section>

      </div>

      <section class="${styles['tickValue-side']} ${styles.tickValue}">${
        sideTickValuesHtml[1]
      }</section>

    </section>
  `;
}

function buildUNSupportedHtml({
  id,
  styles,
  isPCTheme,
  option,
  data,
  isRange,
  speed
}: {
  id: string;
  styles: { [k: string]: string };
  isPCTheme: boolean;
  option: Option;
  data: DataItem[];
  isRange: boolean;
  speed: keyof typeof SPEED_TEXT_MAP;
}) {
  // prettier-ignore
  return `
    <main id="${id}" class="js-timeline-ctx ${styles['timeline-context']} ${styles['unsupported-mode']}">

      <section class="js-operation-box ${styles['operation-box']}">
        <div
          title="${option?.config?.operation?.playButton?.text ?? '不支持播放'}"
          class="${
            styles['timeline-btn-play']
          } js-timeline-btn-play play js-operationButton js-processControl"
          style="display: block"
        ></div>
      </section>

      <section class="js-x-axis-box ${styles['x-axis-box']}">
        <div class="js-unsupported-text ${styles['unsupported-text'] }">${
          option?.config?.axis?.unsupportedText?.content || '只有一期数据暂不支持播放'
        }</div>
      </section>

    </main>
  `;
}

/**
 * 时间轴
 */
// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
export function buildTimeline({
  id,
  styles,
  isPCTheme,
  option,
  data,
  isRange,
  speed
}: {
  id: string;
  styles: { [k: string]: string };
  isPCTheme: boolean;
  option: Option;
  data: DataItem[];
  isRange: boolean;
  speed: keyof typeof SPEED_TEXT_MAP;
}) {
  if (data.length <= 1) {
    return buildUNSupportedHtml({
      id,
      styles,
      isPCTheme,
      option,
      data,
      isRange,
      speed
    });
  }

  const isPCSimpleTheme =
    // @ts-ignore
    isPCTheme && [THEME.PC_SIMPLE, THEME.PC_SIMPLE_DARK].includes(option.theme);
  const axisHtmlStr = buildAxis({
    styles,
    option,
    data,
    isRange
  });
  // * 倍速按钮
  const speedAdjustmentButtonHtml = buildSpeedAdjustmentButton({
    styles,
    isPCTheme,
    option,
    speed
  });

  // prettier-ignore
  return `
    <main id="${id}" class="js-timeline-ctx ${styles['timeline-context']}">

      ${
        isPCSimpleTheme
          ? `
            <section class="js-operation-box ${styles['operation-box']}">

              <div
                title="${option?.config?.operation?.playButton?.text ?? '播放'}"
                class="${
                  styles['timeline-btn-play']
                } js-timeline-btn-play play js-operationButton js-processControl"
                style="display: block"
              ></div>
              <div
                title="${option?.config?.operation?.replayButton?.text ?? '重新播放'}"
                class="${
                  styles['timeline-btn-replay']
                } js-timeline-btn-replay replay js-operationButton js-processControl"
                style="display: none"
              ></div>
              <div
                title="${option?.config?.operation?.pauseButton?.text ?? '暂停'}"
                class="${
                  styles['timeline-btn-pause']
                } js-timeline-btn-pause pause js-operationButton js-processControl"
                style="display: none"
              ></div>

            </section>
          `
          : ''
      }

      ${axisHtmlStr}

      <section class="js-operation-box ${styles['operation-box']}">

        ${
          !isPCSimpleTheme
            ? `
              <div
                title="${option?.config?.operation?.playButton?.text ?? '播放'}"
                class="${
                  styles['timeline-btn-play']
                } js-timeline-btn-play play js-operationButton js-processControl"
                style="display: block"
              ></div>
              <div
                title="${option?.config?.operation?.replayButton?.text ?? '重新播放'}"
                class="${
                  styles['timeline-btn-replay']
                } js-timeline-btn-replay replay js-operationButton js-processControl"
                style="display: none"
              ></div>
              <div
                title="${option?.config?.operation?.pauseButton?.text ?? '暂停'}"
                class="${
                  styles['timeline-btn-pause']
                } js-timeline-btn-pause pause js-operationButton js-processControl"
                style="display: none"
              ></div>
            `
            : ''
        }

        ${!isPCTheme ? speedAdjustmentButtonHtml : ''}

        <div
          title="${option?.config?.operation?.previousButton?.text ?? '上一期'}"
          class="${styles['timeline-btn-step']} ${
    styles['timeline-btn-prev']
  } js-timeline-btn-prev previous js-operationButton ${styles['btn-disabled']} js-btn-disabled"
        >
          ${option?.config?.operation?.previousButton?.text ?? '上一期'}
        </div>

        <div
          title="${option?.config?.operation?.nextButton?.text ?? '下一期'}"
          class="${styles['timeline-btn-step']} ${
    styles['timeline-btn-next']
  } js-timeline-btn-next next js-operationButton"
        >
          ${option?.config?.operation?.nextButton?.text ?? '下一期'}
        </div>

        ${isPCTheme ? speedAdjustmentButtonHtml : ''}

      </section>

    </main>
  `;
}
